import { useTranslation } from "react-i18next";

/**
 * ConflictBanner — week-level load risk banner for the Athlete Plan.
 *
 * Renders one card per conflict surfaced by the backend detector.
 * Severity drives the tone:
 *   watch (amber) → soft caution; coach may want to know
 *   alert (red)   → injury/perf risk; should adjust
 *
 * Empty array → null (parent decides whether to render the slot).
 *
 * Optional onIgnore/onResolve callbacks surface the athlete-side
 * action buttons (Storica's design source has "Игнор" + "Пересмотреть"
 * pair). When both are null the card stays informational.
 */

export type ConflictSeverity = "watch" | "alert";

export interface PlanConflict {
  key: string;
  severity: ConflictSeverity;
  title: string;
  summary: string;
  affected_days: number[];
}

export interface ConflictBannerProps {
  conflicts: PlanConflict[];
  onIgnore?: (conflict: PlanConflict) => void;
  onResolve?: (conflict: PlanConflict) => void;
  className?: string;
}

const TONE_CLASSES: Record<ConflictSeverity, { card: string; icon: string; cta: string }> = {
  watch: {
    card: "border-warn/35 bg-warn/[0.06]",
    icon: "text-warn",
    cta: "bg-warn/15 text-warn border-warn/35 hover:bg-warn/25",
  },
  alert: {
    card: "border-bad/35 bg-bad/[0.06]",
    icon: "text-bad",
    cta: "bg-bad/15 text-bad border-bad/35 hover:bg-bad/25",
  },
};

export function ConflictBanner({
  conflicts,
  onIgnore,
  onResolve,
  className = "",
}: ConflictBannerProps) {
  const { t } = useTranslation("plan");
  if (conflicts.length === 0) return null;

  return (
    <div data-testid="conflict-banner" className={`flex flex-col gap-2 ${className}`}>
      {conflicts.map((c) => {
        const tone = TONE_CLASSES[c.severity] ?? TONE_CLASSES.watch;
        return (
          <div
            key={c.key}
            data-testid={`conflict-banner-item-${c.key}`}
            data-severity={c.severity}
            className={`rounded-card border p-4 flex items-start gap-3 ${tone.card}`}
          >
            <svg
              viewBox="0 0 24 24"
              width="18"
              height="18"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={`flex-shrink-0 mt-0.5 ${tone.icon}`}
              aria-hidden
            >
              <path d="M12 9v4M12 17h.01M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            </svg>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold text-fg-primary leading-tight">
                {c.title}
              </div>
              <div className="mt-1 text-[11px] text-fg-secondary leading-snug">
                {c.summary}
              </div>
            </div>
            {(onIgnore || onResolve) && (
              <div className="flex gap-2 flex-shrink-0">
                {onIgnore && (
                  <button
                    type="button"
                    data-testid={`conflict-banner-ignore-${c.key}`}
                    onClick={() => onIgnore(c)}
                    aria-label={t("conflict_banner.ignore_aria", { defaultValue: "Игнорировать конфликт: {{title}}", title: c.title })}
                    className="px-3 py-1.5 rounded-md border border-bg-border text-[11.5px] text-fg-secondary hover:bg-bg-raised transition"
                  >
                    {t("conflict_banner.ignore", { defaultValue: "Игнор" })}
                  </button>
                )}
                {onResolve && (
                  <button
                    type="button"
                    data-testid={`conflict-banner-resolve-${c.key}`}
                    onClick={() => onResolve(c)}
                    aria-label={t("conflict_banner.resolve_aria", { defaultValue: "Пересмотреть план для конфликта: {{title}}", title: c.title })}
                    className={`px-3 py-1.5 rounded-md border text-[11.5px] font-semibold transition ${tone.cta}`}
                  >
                    {t("conflict_banner.resolve", { defaultValue: "Пересмотреть" })}
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
